import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  getAllValidationErrors,
  getValidationOptions,
} from './sanitizedTextField.utils';
import type { ValidationContext } from './sanitizedTextField.types';

// Since @backstage/plugin-scaffolder-react does not export underlying types it uses from @rjsf/utils
// we need to some acrobatics to get the types we need
export type ErrorSchema = Parameters<SanitizedTextFieldProps['onChange']>[1];

export const sanitizedTextFieldSchema = makeFieldSchema({
  output: zod =>
    // Base validation that will always be enforced
    zod.string().transform(val => val.trim()),

  uiOptions: zod =>
    zod.object({
      // Input styling
      autofocus: zod
        .boolean()
        .optional()
        .describe('Whether to focus this field on load'),
      placeholder: zod
        .string()
        .optional()
        .describe('Placeholder text when field is empty'),
      variant: zod
        .enum(['standard', 'outlined'])
        .optional()
        .default('standard')
        .describe('The visual style of the input field'),

      // Validation customization
      minLength: zod
        .number()
        .optional()
        .default(3)
        .describe('Minimum number of characters required'),
      maxLength: zod
        .number()
        .optional()
        .default(63)
        .describe('Maximum number of characters allowed'),
      allowSpecialChars: zod
        .boolean()
        .optional()
        .default(false)
        .describe('Allow special characters beyond alphanumeric and hyphens'),
      disableUppercase: zod
        .boolean()
        .optional()
        .default(false)
        .describe('Force lowercase characters'),
      disableSpaces: zod
        .boolean()
        .optional()
        .default(false)
        .describe('Disable spaces in input'),

      // Transformation options
      transformToKebabCase: zod
        .boolean()
        .optional()
        .default(false)
        .describe('Convert spaces to hyphens'),

      // Custom regex validation
      customRegex: zod
        .string()
        .refine(
          str => {
            try {
              // eslint-disable-next-line no-new -- We intentionally want to discard the object created
              new RegExp(str);
              return true;
            } catch (e) {
              return false;
            }
          },
          {
            message: 'Invalid regex pattern',
          },
        )
        .optional()
        .describe('Custom regex pattern for additional validation'),
    }),
});

export const sanitizedTextFieldWithOptionsSchema =
  sanitizedTextFieldSchema.schema;

export type SanitizedTextFieldProps = typeof sanitizedTextFieldSchema.TProps;

/**
 * Validates a sanitized text field against the provided options.
 * Returns an array of validation errors.
 *
 * @param value The field value to validate
 * @param validationContext The validation context containing schema and UI options
 * @returns Array of validation error messages
 */
export function validateSanitizedTextField(
  value: string | undefined,
  validationContext: ValidationContext,
): string[] {
  const validationOptions = getValidationOptions(validationContext);
  return getAllValidationErrors(value || '', validationOptions);
}
